import dropRight from 'lodash/dropRight';
import split from 'lodash/split';
import last from 'lodash/last';

import defaultImage from '../../assets/images/placeholders/blank-profile-picture-512@2x.png';

interface FileItemType {
  file: string;
}

class Files {
  static readonly imageExtensions = [
    'jpeg',
    'jpg',
    'png',
    'gif',
    'bmp',
    'webp',
    'tif',
    'tiff'
  ];
  static readonly tableExtensions = ['csv', 'xls', 'ods'];
  static readonly textExtensions = ['docx', 'dox', 'txt', 'odt'];
  static readonly model3dMaxExtensions = ['max'];
  static readonly archiveExtensions = ['zip', 'rar', '7z'];
  static readonly pdfExtensions = ['pdf'];
  static readonly audioExtensions = ['mp3', 'wav'];
  static readonly videoExtensions = ['mp4', 'mov', 'avi'];

  static image(item: FileItemType, version?: string): string {
    const url = Files.url(item, version);

    return this.isImage(url) ? url : defaultImage;
  }

  static defaultVersion(): string {
    return 'thumb_160x160';
  }

  static webPreviewVersion(): string {
    return 'web_preview';
  }

  static extension(file: string): string {
    return file && file.substr(file.lastIndexOf('.') + 1);
  }

  static isImage(file: string): boolean {
    return /(jpe?g|png|gif|bmp|webp|tiff?)/i.test(this.extension(file));
  }

  static isGif(file: string): boolean {
    return /(gif)/i.test(this.extension(file));
  }

  static isTiff(file: string): boolean {
    return /(tiff?)/i.test(this.extension(file));
  }

  static isTableFile(file: string): boolean {
    return /(csv|xls|ods)/i.test(this.extension(file));
  }

  static isTextFile(file: string): boolean {
    return /(doc?x|txt|odt)/i.test(this.extension(file));
  }

  static is3dMaxModel(file: string): boolean {
    return /(max)/i.test(this.extension(file));
  }

  static isArchive(file: string): boolean {
    return /(zip|rar|7z)/i.test(this.extension(file));
  }

  static isPDF(file: string): boolean {
    return /(pdf)/i.test(this.extension(file));
  }

  static isAudio(file: string): boolean {
    return /(mp3|wav)/i.test(this.extension(file));
  }

  static isVideo(file: string): boolean {
    return /(mp4|mov|avi|webm|ogv|wmv|flv|mkv)/i.test(this.extension(file));
  }

  static isPreview3dModel(file: string): boolean {
    return /(glb|gltf)/i.test(this.extension(file));
  }

  static isPreview3dModelGlb(file: string): boolean {
    return /(glb)/i.test(this.extension(file));
  }

  static url(item: FileItemType, version?: string): string {
    const url = item?.file;

    if (url) {
      return this.urlFromFile(url, version);
    }

    return url;
  }

  // Versions of tiff files are generated by the backend as png,
  // so the version file name has to be renamed accordingly
  static versionFileName(fileName: string): string {
    if (!fileName || !this.isTiff(fileName)) {
      return fileName;
    }

    return fileName.replace(/\.tiff?(?=$|[?#])/i, '.png');
  }

  static urlFromFile(file: string, version?: string | 'original'): string {
    if (version === 'original') {
      return file;
    }

    if (file && this.isImage(file)) {
      const urlParts = split(file, '/');

      return [
        ...dropRight(urlParts),
        [
          version || Files.defaultVersion(),
          this.versionFileName(last(urlParts))
        ].join('_')
      ].join('/');
    }

    return file;
  }

  // Browsers can not render tiff originals, but the backend generates a full
  // size png web preview for them, so it is shown instead of the original.
  // The web preview keeps the original name and only changes the extension
  static fullSizeUrlFromFile(file: string): string {
    if (file && this.isTiff(file)) {
      return this.versionFileName(file);
    }

    return file;
  }
}

export default Files;
